import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { Overview } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { Empty, ErrorState, Skeleton, fmtInt } from '../ui/bits.js';
import { FailedBlock } from '../ui/failed.js';

/**
 * The host itself — the operator's screen, and only theirs.
 *
 * `scopeOf()` on the server already draws a hard line between the reload-secret
 * holder and a signed-in account, and the two want different screens rather
 * than the same screen with different sentences. The operator wants to know
 * what is dark and what the whole box is doing; a customer with one site wants
 * that site. Until now both got the fleet screen, with the dark-tenant block and
 * the host-wide call total sitting at the top of a customer's first page.
 *
 * The refusal below is a redirect and not an error: an account that lands here
 * from a bookmark or a shared link has done nothing wrong, and the honest
 * answer is "that screen is not yours" plus the way back. It is not a security
 * boundary and does not pretend to be one — every read behind it is scoped by
 * the server, which answers an account nothing it is not a member of whatever
 * this page renders.
 */
export function HostScreen(props: {
  state: DashboardState;
  actions: DashboardActions;
  now: number;
}): VNode {
  const { state, actions, now } = props;

  if (state.principal?.kind !== 'operator') {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>This host</h1>
        ${Empty({
          title: 'This screen belongs to the operator of this host.',
          body: 'It reports manifests that failed to load and what the whole box is serving — facts about other people’s sites as well as yours. Your own sites are on the Sites screen.',
          action: { label: 'Back to your sites', href: '#/sites' },
        })}
      </section>
    `;
  }

  if (state.error) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>This host</h1>
        ${ErrorState({
          message: state.error,
          label: 'Could not read the registry.',
          onRetry: () => actions.reload(),
        })}
      </section>
    `;
  }

  if (!state.overview) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>This host</h1>
        ${Skeleton({ rows: 4, label: 'Reading the registry…' })}
      </section>
    `;
  }

  const o: Overview = state.overview;

  return html`
    <section class="wrap screen">
      <p class="eyebrow">Operator</p>
      <h1 class="screen__h" tabindex="-1" data-focus-target>This host</h1>
      <p class="screen__p">
        Everything the registry loaded, everything it could not, and what the box served in the
        window. A site owner sees none of this — their screen is${SP}<a href="#/sites">Sites</a>.
      </p>

      <div class="stats">
        <div class="stat">
          <p class="stat__label">Serving</p>
          <p class="stat__value">${fmtInt(o.tenants.length)}</p>
          <p class="stat__note">tenants in the registry</p>
        </div>
        <div class="stat">
          <p class="stat__label">Dark</p>
          <p class=${o.failed.length ? 'stat__value stat__value--warn' : 'stat__value'}>
            ${fmtInt(o.failed.length)}
          </p>
          <p class="stat__note">manifests on disk that did not load</p>
        </div>
        <div class="stat">
          <p class="stat__label">Calls in window</p>
          <p class="stat__value">${fmtInt(o.usage.calls)}</p>
          <p class="stat__note">
            ${o.usage.from && o.usage.to ? html`${o.usage.from} → ${o.usage.to}` : 'window not reported'}${SP}·${SP}
            <a href="#/traffic">every tool, by day</a>
          </p>
        </div>
        <div class="stat">
          <p class="stat__label">Faults</p>
          <p class="stat__value">${fmtInt(o.tenants.reduce((n, t) => n + t.issues, 0))}</p>
          <p class="stat__note">tools advertising a parameter the binding cannot honour</p>
        </div>
      </div>

      ${o.failed.length
        ? FailedBlock({ failed: o.failed, now })
        : Empty({
            title: 'Every manifest on disk loaded.',
            body: 'A tenant whose manifest fails to parse has no registry entry at all — it is absent from the Sites table rather than present and unhealthy — so this is the only place it would ever appear.',
          })}

      <p class="screen__foot">
        The registry is read from <code>tenantsDir</code> at boot and on reload. A manifest that
        parses is served; one that does not is listed here with the parser's own sentence, which is
        the repair instruction.
      </p>
    </section>
  `;
}
